// Planche de démonstration des cosmétiques refaits (outil de développement, aucune donnée de jeu) :
//   /?sheet=cosmetics   matières, motifs, autocollants, tanks complets, et effets animés (auras, traînées, tirs,
//                       éliminations, morts). Sert à faire valider le rendu par Léni avant l'éditeur du hangar.
import { useEffect, useRef } from 'react';
import { Application, Container, Text } from 'pixi.js';
import { getClass, type ClassNode } from '../../../shared/catalog/classes';
import { AURAS, COLLECTIONS, COSMETIC_DEFS, DEATHS, DECALS, DEFAULT_LOOK, KILLS, MATERIALS, PATTERNS, RARITY_COLOR, RARITY_NAME, SHOTS, TRAILS, type CollectionId, type CosmeticDef, type CosmeticSlot, type PartId, type TankLook } from '../../../shared/cosmetics';
import { ACCENT_ENEMY, ACCENT_SELF, ART_R } from '../game/render/art';
import { drawPaintedTank } from '../game/render/paint';
import { Fx, type FxTank } from '../game/render/fx';
import { t, type Key } from '../i18n';

const W = 1440;

/** Couleur de base et couleur de motif de chaque collection (vitrine des collections). */
const THEME: Record<CollectionId, [number, number]> = {
  origine: [0x8b97a5, 0xeef3f8],
  banquise: [0x9ad1ff, 0xffffff],
  volcan: [0x5a3a30, 0xff6a1a],
  cyber: [0x00e5ff, 0xff00c8],
  jungle: [0x5f8f45, 0x2e4a22],
  abysses: [0x1f4f8f, 0x5ff7ff],
  cosmos: [0x6a4cff, 0xffffff],
  empire: [0xe8e2d4, 0xc9a227],
  ferraille: [0x8a6a4a, 0x2b2b2b],
  spectre: [0x9fa8c8, 0x2b2440],
  sakura: [0xc0392b, 0xffc8dd],
  desert: [0xd9b26f, 0x8a5a2b],
  celeste: [0xf4f1ea, 0xe0b84a],
  noel: [0xc8102e, 0xf4f1ea],
};
const pick = <T,>(a: T[], i: number) => a[i % a.length];
/** Objet d'une collection pour un emplacement (le premier trouvé), ou null. */
const ofCol = (c: CollectionId, slot: CosmeticSlot) => COSMETIC_DEFS.find((d) => d.collection === c && d.slot === slot)?.id ?? null;

function look(parts: Partial<Record<PartId, Partial<TankLook['parts'][PartId]>>>, extra: Partial<TankLook> = {}): TankLook {
  const out: TankLook = { ...DEFAULT_LOOK, ...extra, parts: { ...DEFAULT_LOOK.parts } };
  for (const k of Object.keys(parts) as PartId[]) out.parts[k] = { ...DEFAULT_LOOK.parts[k], ...parts[k] };
  return out;
}
const all = (p: Partial<TankLook['parts'][PartId]>, treads?: Partial<TankLook['parts'][PartId]>) => look({ hull: p, turret: p, barrels: { ...p }, treads: treads ?? { ...p, color: 0x2b3038 } });

function tankAt(stage: Container, cls: ClassNode, l: TankLook, x: number, y: number, size: number, accent = ACCENT_SELF, aim = -Math.PI / 4): Container {
  const t = drawPaintedTank(cls, l, accent, Math.round(x + y));
  const root = new Container();
  t.hull.rotation = -Math.PI / 2;
  t.turret.rotation = aim;
  root.addChild(t.hull, t.turret, t.auto);
  root.position.set(x, y);
  root.scale.set(size / ART_R);
  stage.addChild(root);
  return root;
}

function text(stage: Container, s: string, x: number, y: number, size = 12, color = 0xdfe6ee, anchor = 0.5): Text {
  const t = new Text({ text: s, style: { fontFamily: 'system-ui', fontSize: size, fill: color, align: 'center', fontWeight: size > 14 ? '700' : '500' } });
  t.anchor.set(anchor, 0);
  t.position.set(x, y);
  stage.addChild(t);
  return t;
}

function title(stage: Container, s: string, y: number, sub?: string): void {
  text(stage, s, 24, y, 20, 0xffffff, 0);
  if (sub) text(stage, sub, 24, y + 26, 12, 0x93a1b0, 0);
}

function caption(stage: Container, d: CosmeticDef, x: number, y: number): void {
  text(stage, d.name, x, y, 12);
  text(stage, RARITY_NAME[d.rarity], x, y + 15, 10, RARITY_COLOR[d.rarity]);
}

export function CosmeticSheet() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    const app = new Application();
    let destroyed = false;
    void app.init({ width: W, height: 7200, background: 0x151a20, antialias: true, resolution: Math.min(2, window.devicePixelRatio || 1) }).then(() => {
      if (destroyed) return;
      app.canvas.style.width = '100%';
      app.canvas.style.height = 'auto';
      host.appendChild(app.canvas);
      const st = app.stage;
      const base = getClass('BASE');
      const quad = getClass('C01-A1');
      let y = 20;

      title(st, 'Matières', y, 'La couleur de chaque pièce est libre et gratuite ; la matière change la façon dont elle est rendue.');
      y += 70;
      const colors = [0xc0392b, 0x2e86de, 0x27ae60, 0x8e44ad, 0xd35400, 0x16a085, 0xf1c40f, 0x00d2ff, 0xe74c3c];
      MATERIALS.forEach((m, i) => {
        const x = 90 + (i % 9) * 150;
        const yy = y + Math.floor(i / 9) * 150;
        const c = m.collection === 'origine' ? pick(colors, i) : THEME[m.collection][0];
        tankAt(st, base, all({ color: c, mat: m.id }, { color: 0x2b3038, mat: m.id }), x, yy + 50, 46);
        caption(st, m, x, yy + 112);
      });
      y += Math.ceil(MATERIALS.length / 9) * 150 + 20;

      title(st, 'Motifs', y, 'Posés pièce par pièce, dans la couleur de son choix, par-dessus la matière.');
      y += 70;
      const patBase = [0x55606d, 0x3d5a80, 0x6b8f71, 0x7f5539, 0x2d3142, 0x264653, 0xc77d2a, 0x22223b];
      const patCol = [0xeef3f8, 0xf4d35e, 0x2f3e46, 0x2b1d14, 0x00f5d4, 0xe9c46a, 0x1b1b1b, 0xff7b00];
      PATTERNS.forEach((p, i) => {
        const x = 100 + (i % 8) * 170;
        const yy = y + Math.floor(i / 8) * 155;
        const [b0, c0] = p.collection === 'origine' ? [pick(patBase, i), pick(patCol, i)] : THEME[p.collection];
        tankAt(st, base, look({ hull: { color: b0, mat: 'mat:satin', pat: p.id, patColor: c0 }, turret: { color: b0, mat: 'mat:satin', pat: p.id, patColor: c0 }, barrels: { color: b0 }, treads: { color: 0x2b3038, pat: p.id, patColor: c0 } }), x, yy + 50, 50);
        caption(st, p, x, yy + 116);
      });
      y += Math.ceil(PATTERNS.length / 8) * 155 + 20;

      title(st, 'Autocollants', y, 'Sur les chenilles, dans la couleur de son choix ; le dôme garde la couleur du camp et les canons restent lisibles.');
      y += 70;
      DECALS.forEach((d, i) => {
        const x = 80 + (i % 10) * 132;
        const yy = y + Math.floor(i / 10) * 155;
        const dc = d.collection === 'origine' ? pick([0xffd166, 0xeef3f8, 0xef476f, 0x06d6a0, 0xff8c42, 0xeef3f8, 0xa0c4ff, 0xffd166, 0xeef3f8, 0x9bf6ff], i) : THEME[d.collection][0];
        tankAt(st, base, look({ hull: { color: 0x3b4252, mat: 'mat:satin' }, turret: { color: 0x3b4252, mat: 'mat:satin' }, barrels: { color: 0x2e3440 }, treads: { color: 0x22262e, dec: d.id, decColor: dc } }), x, yy + 56, 50, ACCENT_SELF, -Math.PI / 4);
        caption(st, d, x, yy + 116);
      });
      y += Math.ceil(DECALS.length / 10) * 155 + 20;

      title(st, 'Tanks complets', y, 'Exemples de combinaisons (le cœur de la tourelle est à la couleur du camp : cyan pour soi, rouge pour un ennemi).');
      y += 70;
      const full: [string, TankLook, number][] = [
        ['C01-A1', look({ hull: { color: 0x1c1f24, mat: 'mat:carbone', pat: 'pat:flammes', patColor: 0xff5a1f }, turret: { color: 0x22262c, mat: 'mat:carbone' }, barrels: { color: 0xb9c2cc, mat: 'mat:chrome' }, treads: { color: 0x15171b, mat: 'mat:mat' } }), ACCENT_SELF],
        ['C06-F', look({ hull: { color: 0x6a4c93, mat: 'mat:nacre', pat: 'pat:ecailles', patColor: 0xffffff }, turret: { color: 0x8e6bbf, mat: 'mat:nacre' }, barrels: { color: 0x4b3a6b, mat: 'mat:satin' }, treads: { color: 0x2b2340, dec: 'dec:etoile', decColor: 0xffd6ff } }), ACCENT_SELF],
        ['C12-F', look({ hull: { color: 0xc9a227, mat: 'mat:or' }, turret: { color: 0xc9a227, mat: 'mat:or' }, barrels: { color: 0x2b2b2b, mat: 'mat:brosse' }, treads: { color: 0x1e1e1e, dec: 'dec:couronne', decColor: 0xffd166 } }), ACCENT_ENEMY],
        ['C03-F', look({ hull: { color: 0x4f6d3a, mat: 'mat:mat', pat: 'pat:camo', patColor: 0x2e3b22 }, turret: { color: 0x4f6d3a, pat: 'pat:camo', patColor: 0x2e3b22 }, barrels: { color: 0x3d4f2c }, treads: { color: 0x2a2f22, dec: 'dec:chevrons', decColor: 0xe9d8a6 } }), ACCENT_ENEMY],
        ['C05-F', look({ hull: { color: 0x00e5ff, mat: 'mat:neon', pat: 'pat:circuit', patColor: 0x00e5ff }, turret: { color: 0xff00c8, mat: 'mat:neon' }, barrels: { color: 0x00e5ff, mat: 'mat:neon' }, treads: { color: 0x7a00ff, mat: 'mat:neon' } }), ACCENT_SELF],
        ['C09-B', look({ hull: { color: 0xff5a1f, mat: 'mat:magma' }, turret: { color: 0xff5a1f, mat: 'mat:magma' }, barrels: { color: 0x2a1d18, mat: 'mat:carbone' }, treads: { color: 0x1d1512, dec: 'dec:dragon', decColor: 0xffb703 } }), ACCENT_ENEMY],
      ];
      full.forEach(([id, l, accent], i) => {
        const x = 120 + i * 240;
        tankAt(st, getClass(id), l, x, y + 70, 64, accent, -Math.PI / 3);
        text(st, getClass(id).name, x, y + 160, 13);
      });
      y += 210;

      // ─── Effets animés ───────────────────────────────────────────────
      title(st, 'Effets', y, 'Auras (1re ligne) et traînées (2e ligne) sur des tanks qui roulent ; tirs, éliminations et morts en boucle (3e ligne).');
      y += 70;
      const fx = new Fx(app.renderer);
      const fxStage = new Container();
      st.addChild(fx.below, fxStage, fx.root);
      type Mover = { root: Container; t: FxTank; cx: number; cy: number; ph: number; aura: string | null; trail: string | null };
      const movers: Mover[] = [];
      const addMover = (aura: string | null, trail: string | null, cx: number, cy: number, l: TankLook, cls: ClassNode, label: CosmeticDef) => {
        const root = tankAt(fxStage, cls, l, cx, cy, 30);
        movers.push({ root, t: { x: cx, y: cy, vx: 0, vy: 0, r: 30, hull: 0 }, cx, cy, ph: movers.length, aura, trail });
        caption(st, label, cx, cy + 70);
      };
      const ROW = 200;
      AURAS.forEach((a, i) => addMover(a.id, null, 130 + (i % 6) * 235, y + 70 + Math.floor(i / 6) * ROW, all({ color: a.collection === 'origine' ? pick([0xc9a227, 0x9ad1ff, 0xd35400, 0x6b8f71, 0x2e86de, 0x6a4c93], i) : THEME[a.collection][0], mat: 'mat:satin' }), base, a));
      const yTrail = y + 70 + Math.ceil(AURAS.length / 6) * ROW;
      TRAILS.forEach((tr, i) => addMover(null, tr.id, 130 + (i % 6) * 235, yTrail + Math.floor(i / 6) * ROW, all({ color: tr.collection === 'origine' ? pick([0x8d6e63, 0x00d2ff, 0x48cae4, 0xff5a1f, 0x22223b, 0xeeeeee], i) : THEME[tr.collection][0], mat: i === 5 ? 'mat:nacre' : 'mat:satin' }), base, tr));
      const yShots = yTrail + Math.ceil(TRAILS.length / 6) * ROW - 30;
      type Demo = { x: number; y: number; kind: 'shot' | 'kill' | 'death'; id: string; every: number; acc: number; root?: Container; cls: ClassNode };
      const demos: Demo[] = [];
      [...SHOTS.map((d) => ['shot', d] as const), ...KILLS.map((d) => ['kill', d] as const), ...DEATHS.map((d) => ['death', d] as const)].forEach(([kind, d], i) => {
        const x = 90 + (i % 12) * 115;
        const yy = yShots + Math.floor(i / 12) * 150 + (i % 2) * 20;
        const demo: Demo = { x, y: yy + 40, kind, id: d.id, every: kind === 'shot' ? 0.6 : 1.8, acc: i * 0.15, cls: base };
        if (kind !== 'kill') demo.root = tankAt(fxStage, kind === 'shot' ? quad : base, all({ color: 0x5f6976, mat: 'mat:brosse' }), x, yy + 40, 24, ACCENT_SELF, kind === 'shot' ? 0 : -Math.PI / 4);
        demos.push(demo);
        text(st, `${kind === 'shot' ? 'Tir' : kind === 'kill' ? 'Élimination' : 'Mort'}`, x, yy + 88, 10, 0x93a1b0);
        caption(st, d, x, yy + 102);
      });
      const nDemo = SHOTS.length + KILLS.length + DEATHS.length;
      let yCol = yShots + Math.ceil(nDemo / 12) * 150 + 40;

      // ─── Vitrine des collections : un tank par collection, tout l'ensemble porté ─────
      title(st, 'Collections', yCol, 'Chaque collection assortit matière, motif, autocollant, aura et traînée (tirs, éliminations et morts plus haut).');
      yCol += 80;
      COLLECTIONS.filter((c) => c !== 'origine').forEach((c, i) => {
        const [b0, c0] = THEME[c];
        const mat = ofCol(c, 'mat') ?? 'mat:satin';
        const pat = ofCol(c, 'pat');
        const p = { color: b0, mat, pat, patColor: c0 };
        const dark = c0 === 0x2b2b2b || c0 === 0x2b2440;
        const l = look({ hull: p, turret: { color: b0, mat }, barrels: { color: b0, mat }, treads: { color: 0x2b3038, mat, dec: ofCol(c, 'dec'), decColor: dark ? b0 : c0 } });
        const cx = 130 + (i % 6) * 235;
        const cy = yCol + Math.floor(i / 6) * 230;
        const root = tankAt(fxStage, base, l, cx, cy, 30);
        movers.push({ root, t: { x: cx, y: cy, vx: 0, vy: 0, r: 30, hull: 0 }, cx, cy, ph: movers.length, aura: ofCol(c, 'aura'), trail: ofCol(c, 'trail') });
        text(st, t(`col_${c}` as Key), cx, cy + 70, 14);
        text(st, `${COSMETIC_DEFS.filter((d) => d.collection === c).length} objets`, cx, cy + 88, 10, 0x93a1b0);
      });
      yCol += Math.ceil((COLLECTIONS.length - 1) / 6) * 230;
      app.renderer.resize(W, yCol + 40);
      const counter = text(st, '', W - 24, 20, 11, 0x93a1b0, 1);

      let last = performance.now();
      app.ticker.add(() => {
        const now = performance.now();
        const dt = Math.min(0.05, (now - last) / 1000);
        last = now;
        const T = now / 1000;
        for (const m of movers) {
          const a = T * 0.9 + m.ph;
          const nx = m.cx + Math.cos(a) * 70;
          const ny = m.cy + Math.sin(a * 2) * 32;
          m.t.vx = (nx - m.t.x) / Math.max(dt, 1e-3);
          m.t.vy = (ny - m.t.y) / Math.max(dt, 1e-3);
          m.t.x = nx;
          m.t.y = ny;
          m.root.position.set(nx, ny);
          m.root.children[0].rotation = Math.atan2(m.t.vy, m.t.vx);
          fx.aura(String(m.ph), m.aura, m.t, dt);
          fx.trail(String(m.ph), m.trail, m.t, dt);
        }
        for (const d of demos) {
          d.acc += dt;
          if (d.root && d.kind === 'death') d.root.visible = d.acc % d.every < d.every * 0.55;
          if (d.acc < d.every) continue;
          d.acc -= d.every;
          if (d.kind === 'shot') {
            for (const b of quad.weapon.barrels) {
              const ang = (b.angle * Math.PI) / 180;
              fx.shot(d.id, d.x + Math.cos(ang) * b.length * 24, d.y + Math.sin(ang) * b.length * 24, ang, 18);
            }
          } else if (d.kind === 'kill') fx.kill(d.id, d.x, d.y, 18);
          else fx.death(d.id, d.x, d.y, 24, [0x5f6976, 0x8b97a5, 0x3a414b]);
        }
        fx.update(dt);
        counter.text = `${fx.count} particules`;
      });
    });
    return () => {
      destroyed = true;
      app.destroy({ removeView: true });
    };
  }, []);
  return <div ref={ref} style={{ overflow: 'auto', height: '100%' }} data-testid="cosmetic-sheet" />;
}
